<script setup lang="ts">
import { type HTMLAttributes, type Component, computed, ref, useAttrs } from 'vue'
import { useVModel } from '@vueuse/core'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { HelpCircle } from 'lucide-vue-next'
import AppTooltip from './AppTooltip.vue'
import { FORM_CONTROL_SIZE } from '@/shared/constants/form-control-sizes'

defineOptions({
  inheritAttrs: false
})

const attrs = useAttrs()

const inputVariants = cva('transition-all duration-200', {
  variants: {
    variant: {
      default: 'focus-visible:ring-0',
      outlined:
        'bg-transparent border-2 border-border focus-visible:ring-0 focus-visible:border-primary',
      filled:
        'bg-muted/50 border-2 border-transparent focus-visible:ring-0 focus-visible:border-primary',
      subtle: 'bg-muted/50 border-2 border-border focus-visible:ring-0 focus-visible:border-primary'
    },
    inputSize: {
      default: FORM_CONTROL_SIZE.default,
      sm: `${FORM_CONTROL_SIZE.sm} px-3`,
      md: FORM_CONTROL_SIZE.md,
      lg: FORM_CONTROL_SIZE.lg
    }
  },
  defaultVariants: {
    variant: 'subtle',
    inputSize: 'default'
  }
})

const suffixContainerVariants = cva(
  'relative flex items-stretch overflow-hidden transition-all duration-200',
  {
    variants: {
      variant: {
        default: 'border border-input bg-background focus-within:ring-2 focus-within:ring-ring',
        outlined: 'bg-transparent border-2 border-border focus-within:border-primary',
        filled: 'bg-muted/50 border-2 border-transparent focus-within:border-primary',
        subtle: 'bg-muted/50 border-2 border-border focus-within:border-primary'
      },
      inputSize: {
        default: FORM_CONTROL_SIZE.default,
        sm: `${FORM_CONTROL_SIZE.sm} px-3`,
        md: FORM_CONTROL_SIZE.md,
        lg: FORM_CONTROL_SIZE.lg
      }
    },
    defaultVariants: {
      variant: 'subtle',
      inputSize: 'default'
    }
  }
)

type InputVariants = VariantProps<typeof inputVariants>

interface Props {
  label?: string
  /** Secondary label text (e.g. template inherit hint), shown to the right of the label. */
  labelHint?: string
  /** Classes for labelHint. Defaults to warning color. */
  labelHintClass?: HTMLAttributes['class']
  required?: boolean
  error?: string
  helperText?: string
  tooltip?: string
  leftIcon?: Component
  rightIcon?: Component
  suffix?: string
  containerClass?: HTMLAttributes['class']
  class?: HTMLAttributes['class']
  variant?: InputVariants['variant']
  inputSize?: InputVariants['inputSize']
  type?: string
  placeholder?: string
  disabled?: boolean
  modelValue?: string | number | null
  defaultValue?: string | number | null
  labelClass?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  disabled: false,
  variant: 'subtle',
  inputSize: 'default',
  labelHintClass: 'text-warning'
})

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string | number): void
}>()

const inputRef = ref<{ $el: HTMLInputElement } | null>(null)

const modelValue = useVModel(props, 'modelValue', emits, {
  passive: true,
  defaultValue: props.defaultValue
})

const hasError = computed(() => !!props.error)

const inputId = computed(
  () => (attrs.id as string) || `input-${Math.random().toString(36).substring(2, 9)}`
)

const shouldTrim = computed(() => {
  if (props.type === 'password' || props.type === 'file') return false
  const idOrName = String(attrs.id || attrs.name || '').toLowerCase()
  if (idOrName.includes('password')) return false
  return true
})

const trimCurrentValue = (target?: HTMLInputElement | null) => {
  if (!shouldTrim.value) return

  const el = target || (inputRef.value?.$el as HTMLInputElement | undefined)
  if (el && typeof el.value === 'string') {
    const trimmed = el.value.trim()
    if (el.value !== trimmed) {
      el.value = trimmed
    }
  }

  if (typeof modelValue.value === 'string') {
    const trimmed = modelValue.value.trim()
    if (modelValue.value !== trimmed) {
      modelValue.value = trimmed
    }
  }
}

const handleInput = (event: Event) => {
  if (!shouldTrim.value) return
  const target = event.target as HTMLInputElement | null
  if (target && typeof target.value === 'string' && /^\s+/.test(target.value)) {
    target.value = target.value.replace(/^\s+/, '')
    modelValue.value = target.value
  }
}

const handleBlur = (event: FocusEvent) => {
  trimCurrentValue(event.target as HTMLInputElement)
}

const handleChange = (event: Event) => {
  trimCurrentValue(event.target as HTMLInputElement)
}

const modelValueProxy = computed({
  get: () => modelValue.value as string | number,
  set: (val) => {
    let nextVal = val
    if (typeof nextVal === 'string' && shouldTrim.value) {
      nextVal = nextVal.replace(/^\s+/, '')
    }
    modelValue.value = nextVal
  }
})

const handleInputClick = (event: MouseEvent) => {
  if (props.disabled) return
  const target = event.currentTarget as HTMLInputElement
  if (['date', 'datetime-local', 'time'].includes(props.type)) {
    if (typeof target.showPicker === 'function') {
      try {
        target.showPicker()
      } catch (error) {
        console.error('Failed to show picker:', error)
      }
    }
  }
}

const handleWheel = (event: WheelEvent) => {
  if (props.type === 'number') {
    ;(event.currentTarget as HTMLInputElement | null)?.blur()
  }
}
defineExpose({
  trim: () => trimCurrentValue()
})
</script>

<template>
  <div :class="cn('flex flex-col gap-1.5 w-full', props.containerClass)">
    <div
      v-if="props.label || props.labelHint || $slots['label-right']"
      class="flex items-center justify-between gap-2"
    >
      <Label
        v-if="props.label"
        :for="inputId"
        :class="
          cn(
            'text-sm font-medium transition-colors flex items-center gap-1.5',
            hasError ? 'text-destructive' : 'text-foreground',
            props.labelClass
          )
        "
      >
        <span>{{ props.label }}</span>
        <span v-if="props.required" class="text-destructive ml-0.5">*</span>
        <AppTooltip v-if="props.tooltip" :text="props.tooltip">
          <HelpCircle
            class="h-3.5 w-3.5 text-muted-foreground/75 cursor-help hover:text-primary transition-colors shrink-0"
          />
        </AppTooltip>
      </Label>
      <small
        v-if="props.labelHint"
        :class="cn('text-xs font-medium shrink-0', props.labelHintClass)"
      >
        {{ props.labelHint }}
      </small>
      <slot name="label-right" />
    </div>

    <div
      :class="
        cn(
          props.suffix
            ? [
                suffixContainerVariants({ variant: props.variant, inputSize: props.inputSize }),
                hasError && 'border-destructive focus-within:border-destructive',
                props.disabled && 'opacity-50 cursor-not-allowed'
              ]
            : 'relative flex items-center'
        )
      "
    >
      <!-- Left Icon/Slot -->
      <div
        v-if="props.leftIcon || $slots.left"
        :class="
          cn(
            'flex items-center justify-center text-muted-foreground shrink-0',
            $slots.left ? 'pointer-events-auto z-10' : 'pointer-events-none',
            props.suffix
              ? cn(
                  'border-r border-border bg-muted/30 h-full',
                  props.inputSize === 'sm' ? 'w-9' : 'w-11'
                )
              : cn(
                  'absolute top-1/2 -translate-y-1/2',
                  props.inputSize === 'sm' ? 'left-2.5' : 'left-3'
                )
          )
        "
      >
        <slot name="left">
          <component :is="props.leftIcon" :size="props.inputSize === 'sm' ? 14 : 18" />
        </slot>
      </div>

      <Input
        :id="inputId"
        ref="inputRef"
        v-model="modelValueProxy"
        :type="props.type"
        :disabled="props.disabled"
        :placeholder="props.placeholder"
        @wheel="handleWheel"
        :class="
          cn(
            props.suffix
              ? [
                  '!h-full !min-h-0 flex-1 rounded-none border-0 bg-transparent px-3 py-0 text-sm focus-visible:ring-0 focus-visible:border-transparent focus-visible:outline-none shadow-none',
                  props.inputSize === 'sm' && 'px-2 text-xs',
                  props.class
                ]
              : [
                  inputVariants({ variant: props.variant, inputSize: props.inputSize }),
                  (props.leftIcon || $slots.left) && (props.inputSize === 'sm' ? 'pl-8' : 'pl-10'),
                  (props.rightIcon || $slots.right) &&
                    (props.inputSize === 'sm' ? 'pr-8' : 'pr-10'),
                  hasError &&
                    'border-destructive focus-visible:ring-0 focus-visible:border-destructive',
                  props.class
                ]
          )
        "
        @input="handleInput"
        @blur="handleBlur"
        @change="handleChange"
        v-bind="$attrs"
        @click="handleInputClick"
      />

      <div
        v-if="props.suffix"
        :class="
          cn(
            'flex items-center border-l border-border bg-muted/30 px-3 text-sm font-medium text-muted-foreground shrink-0 h-full select-none',
            props.inputSize === 'sm' && 'px-2 text-xs'
          )
        "
      >
        {{ props.suffix }}
      </div>

      <!-- Right Icon/Slot -->
      <div
        v-if="!props.suffix && (props.rightIcon || $slots.right)"
        :class="
          cn(
            'absolute top-1/2 -translate-y-1/2 text-muted-foreground flex items-center justify-center',
            $slots.right ? 'pointer-events-auto z-10' : 'pointer-events-none',
            props.inputSize === 'sm' ? 'right-2.5' : 'right-3'
          )
        "
      >
        <slot name="right">
          <component
            :is="props.rightIcon"
            :size="props.inputSize === 'sm' ? 14 : 18"
            class="pointer-events-none"
          />
        </slot>
      </div>
    </div>

    <!-- Message Container (Prevents layout shift) -->
    <div class="min-h-[5px] mt-1 px-0.5">
      <p
        v-if="hasError"
        class="text-xs font-medium text-destructive animate-in fade-in slide-in-from-top-1"
      >
        {{ props.error }}
      </p>

      <p v-else-if="props.helperText" class="text-xs text-muted-foreground">
        {{ props.helperText }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Add a subtle glow on focus if not error */
:deep(input:focus-visible:not(.border-destructive)) {
  box-shadow: 0 0 0 2px hsl(var(--ring) / 0.1);
}
</style>
